import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster, toast } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OfflineProvider } from "@/context/OfflineContext";
import { ensureAppVersion } from "@/lib/appVersion";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import { Skeleton } from "@/components/ui/skeleton";
import InactivityGuard from "@/components/InactivityGuard";
import ErrorBoundary from "@/components/ErrorBoundary";

// ---------------------------------------------------------------------------
// Route-based code splitting.
//
// Every page below is lazily imported so the initial bundle only contains the
// shell + login. Each route element is wrapped by a per-route ErrorBoundary,
// which means a runtime error inside one page can NEVER blank the whole app —
// the sidebar/nav stays alive and the user can navigate away.
// ---------------------------------------------------------------------------
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Lots = lazy(() => import("@/pages/Lots"));
const LotDetail = lazy(() => import("@/pages/LotDetail"));
const Dispatch = lazy(() => import("@/pages/Dispatch"));
const DispatchHistory = lazy(() => import("@/pages/DispatchHistory"));
const GoodsReturn = lazy(() => import("@/pages/GoodsReturn"));
const StockTransfer = lazy(() => import("@/pages/StockTransfer"));
const RollInventory = lazy(() => import("@/pages/RollInventory"));
const InventoryAdjustment = lazy(() => import("@/pages/InventoryAdjustment"));
const DamageRegister = lazy(() => import("@/pages/DamageRegister"));
const ExcelUpload = lazy(() => import("@/pages/ExcelUpload"));
const AuditLogs = lazy(() => import("@/pages/AuditLogs"));
const DatabaseMaintenance = lazy(() => import("@/pages/DatabaseMaintenance"));
// Production Module
const ProductionDashboard = lazy(() => import("@/pages/production/ProductionDashboard"));
const GreigeStock = lazy(() => import("@/pages/production/GreigeStock"));
const Factories = lazy(() => import("@/pages/production/Factories"));
const IssueGreige = lazy(() => import("@/pages/production/IssueGreige"));
const ProductionLots = lazy(() => import("@/pages/production/ProductionLots"));
const ProductionLotDetail = lazy(() => import("@/pages/production/ProductionLotDetail"));
const ProductionReports = lazy(() => import("@/pages/production/ProductionReports"));
const FactoryPerformance = lazy(() => import("@/pages/production/FactoryPerformance"));
const LotDocuments = lazy(() => import("@/pages/production/LotDocuments"));
// Admin & analytics
const UserManagement = lazy(() => import("@/pages/UserManagement"));
const DBMaintenance = lazy(() => import("@/pages/DBMaintenance"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const LotMapping = lazy(() => import("@/pages/LotMapping"));
const Clients = lazy(() => import("@/pages/Clients"));
const ClientDashboard = lazy(() => import("@/pages/ClientDashboard"));
const BusinessDashboard = lazy(() => import("@/pages/BusinessDashboard"));
const MainDashboard = lazy(() => import("@/pages/MainDashboard"));
const AIBusinessAdvisor = lazy(() => import("@/pages/AIBusinessAdvisor"));
const SalesOrders = lazy(() => import("@/pages/SalesOrders"));

// Suppress ResizeObserver loop error (known browser issue with dropdowns/modals)
// This error is benign and doesn't affect functionality
if (typeof window !== 'undefined') {
  const resizeObserverErr = window.onerror;
  window.onerror = function (message, source, lineno, colno, error) {
    if (message && message.toString().includes('ResizeObserver loop')) {
      return true; // Suppress the error
    }
    return resizeObserverErr ? resizeObserverErr(message, source, lineno, colno, error) : false;
  };

  // Also handle unhandled promise rejections for ResizeObserver
  window.addEventListener('error', (e) => {
    if (e.message && e.message.includes('ResizeObserver loop')) {
      e.stopPropagation();
      e.preventDefault();
    }
  });
}

// Register service worker with proper update detection
function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');

      // If there's already a waiting worker on first load, prompt immediately.
      if (registration.waiting && navigator.serviceWorker.controller) {
        promptUpdate(registration.waiting);
      }

      // Detect a new SW being installed after first load.
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            promptUpdate(newWorker);
          }
        });
      });

      // Once the SW takes control, reload exactly once so the new bundle is used.
      let didReload = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (didReload) return;
        didReload = true;
        window.location.reload();
      });

      // Periodically check for updates so users get fixes within minutes.
      setInterval(() => registration.update().catch(() => {}), 5 * 60 * 1000);
    } catch (err) {
      // Non-critical
      console.warn('[SW] registration failed:', err);
    }
  });
}

function promptUpdate(worker) {
  toast.info('A new version is available', {
    description: 'Refresh to get the latest update.',
    duration: 600000, // sticky until clicked
    action: {
      label: 'Refresh',
      onClick: () => worker.postMessage({ type: 'SKIP_WAITING' }),
    },
  });
}

function ProtectedRoute({ children, adminOnly = false, allowedRoles = null }) {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Skeleton className="h-12 w-12 rounded-lg mx-auto mb-4" />
          <Skeleton className="h-4 w-32 mx-auto" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Role-based access control
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // production_user should redirect to production dashboard
    if (user.role === "production_user") {
      return <Navigate to="/production" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
}

// AccessDenied component for blocked pages
function AccessDenied() {
  const { user } = useAuth();

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h2>
        <p className="text-slate-500 mb-4">
          {user?.role === "production_user"
            ? "Production users can only access the Production module."
            : "You don't have permission to access this page."}
        </p>
        <a href={user?.role === "production_user" ? "/production" : "/"}
           className="text-indigo-600 hover:underline">
          Go to {user?.role === "production_user" ? "Production" : "Dashboard"}
        </a>
      </div>
    </div>
  );
}

// Skeleton shown while a lazy-loaded route chunk is being fetched.
// Kept intentionally minimal so it doesn't flash noticeably on fast networks.
function RouteFallback() {
  return (
    <div className="p-6 space-y-4" data-testid="route-suspense-fallback">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-72" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

/**
 * Wraps a lazily-loaded page element so:
 *   • A React error inside it cannot blank the whole app (per-route boundary).
 *   • The user sees a loading skeleton, not a white screen, while the chunk
 *     is being downloaded (Suspense fallback).
 */
function RouteElement({ label, children }) {
  return (
    <ErrorBoundary variant="route" label={label}>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  useEffect(() => {
    // 1) Clean any stale browser caches/storage on version upgrade BEFORE
    //    registering the new service worker.
    (async () => {
      try { await ensureAppVersion(); } catch { /* ignore */ }
      registerServiceWorker();
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Skeleton className="h-12 w-12 rounded-lg mx-auto mb-4" />
          <Skeleton className="h-4 w-32 mx-auto" />
        </div>
      </div>
    );
  }

  const isProdUser = user?.role === "production_user";
  const isAdminUser = user?.role === "Admin";

  // Small helper: gate a lazy element behind role, keep boundary wrapping intact
  const gated = (allowed, label, node) => (
    <RouteElement label={label}>{allowed ? node : <AccessDenied />}</RouteElement>
  );

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={isProdUser ? "/production" : "/"} replace /> : <Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Landing page routing:
              • production_user      → /production (unchanged)
              • Admin                → Main Dashboard (Business Command
                                       Center visuals — operational KPIs,
                                       Dispatch Trend, Category Mix, Alerts,
                                       Top Clients/Lots, Stock Composition,
                                       Dispatch vs Purchase).
              • Staff / dispatch_user → operational Dashboard (reconciliation
                                       banner + KPIs). They lack access to
                                       profitability by design, so surfacing
                                       the command center to them would look
                                       empty.
              The full management/MIS report (Price Analytics, Profitability,
              etc.) lives at /business-dashboard for admins. */}
        <Route index element={
          isProdUser ? <Navigate to="/production" replace />
          : isAdminUser
            ? <RouteElement label="Dashboard"><MainDashboard /></RouteElement>
            : <RouteElement label="Dashboard"><Dashboard /></RouteElement>
        } />
        {/* Legacy operational view still reachable at /operations for admins
            who want the reconciliation panel etc. */}
        <Route path="operations" element={<RouteElement label="Operations"><Dashboard /></RouteElement>} />

        {/* Non-Production routes - blocked for production_user */}
        <Route path="lots" element={gated(!isProdUser, "Lots", <Lots />)} />
        <Route path="lots/:lotId" element={gated(!isProdUser, "Lot Detail", <LotDetail />)} />
        <Route path="dispatch" element={gated(!isProdUser, "Dispatch", <Dispatch />)} />
        <Route path="dispatch-history" element={gated(!isProdUser, "Dispatch History", <DispatchHistory />)} />
        <Route path="sales-orders" element={gated(!isProdUser, "Sales Orders", <SalesOrders />)} />
        <Route path="goods-return" element={gated(!isProdUser, "Goods Return", <GoodsReturn />)} />
        <Route path="stock-transfer" element={gated(!isProdUser, "Stock Transfer", <StockTransfer />)} />
        <Route path="roll-inventory" element={gated(!isProdUser, "Roll Inventory", <RollInventory />)} />
        <Route path="inventory-adjustment" element={gated(!isProdUser, "Inventory Adjustment", <InventoryAdjustment />)} />
        <Route path="damage-register" element={gated(!isProdUser, "Damage Register", <DamageRegister />)} />
        <Route path="upload" element={gated(!isProdUser, "Excel Upload", <ExcelUpload />)} />
        <Route path="audit-logs" element={gated(!isProdUser, "Audit Logs", <AuditLogs />)} />
        <Route path="database-maintenance" element={gated(!isProdUser, "Database Maintenance", <DatabaseMaintenance />)} />

        {/* Production Module - accessible by all authenticated users */}
        <Route path="production" element={<RouteElement label="Production"><ProductionDashboard /></RouteElement>} />
        <Route path="production/greige-stock" element={<RouteElement label="Greige Stock"><GreigeStock /></RouteElement>} />
        <Route path="production/factories" element={<RouteElement label="Factories"><Factories /></RouteElement>} />
        <Route path="production/issue" element={<RouteElement label="Issue Greige"><IssueGreige /></RouteElement>} />
        <Route path="production/lots" element={<RouteElement label="Production Lots"><ProductionLots /></RouteElement>} />
        <Route path="production/lots/:lotId" element={<RouteElement label="Production Lot Detail"><ProductionLotDetail /></RouteElement>} />
        <Route path="production/reports" element={<RouteElement label="Production Reports"><ProductionReports /></RouteElement>} />
        <Route path="production/performance" element={<RouteElement label="Factory Performance"><FactoryPerformance /></RouteElement>} />
        <Route path="production/lot-documents" element={<RouteElement label="Lot Documents"><LotDocuments /></RouteElement>} />

        {/* User Management - Admin only */}
        <Route path="user-management" element={gated(isAdminUser, "User Management", <UserManagement />)} />
        <Route path="change-password" element={<RouteElement label="Change Password"><ChangePassword /></RouteElement>} />

        {/* Lot Mapping & Client Dashboard - Admin only */}
        <Route path="lot-mapping" element={gated(isAdminUser, "Lot Mapping", <LotMapping />)} />
        <Route path="clients" element={gated(isAdminUser, "Clients", <Clients />)} />
        <Route path="clients/:partyName" element={gated(isAdminUser, "Client Dashboard", <ClientDashboard />)} />
        <Route path="business-dashboard" element={gated(isAdminUser, "Business Dashboard", <BusinessDashboard />)} />
        <Route path="ai-advisor" element={gated(isAdminUser, "AI Business Advisor", <AIBusinessAdvisor />)} />
        <Route path="db-maintenance" element={gated(isAdminUser, "DB Maintenance", <DBMaintenance />)} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <OfflineProvider>
            <InactivityGuard>
              <AppRoutes />
            </InactivityGuard>
          </OfflineProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

export default App;
